// Kanal-Bilanz-Tab: kostenlose vs. bezahlte Kanäle im direkten Vergleich.
// Liest /api/marketing/kanal-bilanz (neuester Lauf von claude_kanal_bilanz.mjs).
// Zwei Blöcke: Gegenüberstellung als Balken + Kanaltabelle mit Ergebnisspalte.
import type { MarketingDeps } from "./marketing.js";

interface Kanal {
  kanal: string; gruppe: "kostenlos" | "bezahlt";
  sessions: number; kaeufe: number; umsatz: number; kosten: number;
}
interface Gruppe {
  gruppe: "kostenlos" | "bezahlt";
  sessions: number; kaeufe: number; umsatz: number; kosten: number;
  ergebnis: number; roas: number | null;
}
interface Plattform {
  name: string; kosten: number; kaeufe: number; wert: number;
  ergebnis: number; roas: number | null; ga4Kaeufe: number; ga4Umsatz: number;
}
interface PlattformSumme { kosten: number; kaeufe: number; wert: number; ergebnis: number; roas: number | null }
interface BilanzResponse {
  empty?: boolean; fehlt?: boolean; von: string; bis: string; tage: number;
  kanaele: Kanal[]; gruppen: Gruppe[]; plattform?: Plattform[]; plattformSumme?: PlattformSumme;
  hinweise: string[]; ohneKaufTracking?: string[]; report_at: string;
}

const FARBE = { kostenlos: "#30d158", bezahlt: "#ff9f0a" };

// GA4 liefert die Kanalgruppen englisch; die Suite ist durchgehend deutsch.
const LABEL: Record<string, string> = {
  "Organic Search": "Organische Suche",
  "Paid Search": "Google Ads (Suche)",
  "Paid Social": "Meta Ads",
  "Paid Other": "Sonstige Anzeigen",
  "Cross-network": "Google Performance Max",
  "Organic Social": "Social organisch",
  "Organic Video": "Video organisch",
  "Organic Shopping": "Shopping organisch",
  "AI Assistant": "KI-Assistenten (ChatGPT & Co.)",
  "Direct": "Direkteinstieg",
  "Unassigned": "Nicht zugeordnet",
  "Referral (fremd)": "Empfehlungen (fremde Seiten)",
};
const label = (k: string): string => LABEL[k] ?? k;

function eur(n: number): string {
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function zahl(n: number): string {
  return Math.round(n).toLocaleString("de-DE");
}

function div(className: string, text?: string): HTMLDivElement {
  const d = document.createElement("div");
  d.className = className;
  if (text !== undefined) d.textContent = text;
  return d;
}

// Gegenüberstellung: je Gruppe eine Karte mit Umsatz-, Kosten- und Ergebnisbalken.
// Balkenlänge relativ zum größten Umsatz beider Gruppen — so ist der Vergleich
// zwischen den Karten ablesbar, nicht nur innerhalb einer.
function renderVergleich(host: HTMLElement, gruppen: Gruppe[], ps?: PlattformSumme, luecke?: string[]): void {
  host.textContent = "";
  // Warnung nur zeigen, solange wirklich Portale ohne Kauf-Ereignisse dabei sind —
  // sonst steht sie noch da, wenn die Lücke längst geschlossen ist.
  const fehlende = luecke?.length
    ? (luecke.length > 1 ? luecke.slice(0, -1).join(", ") + " und " + luecke[luecke.length - 1] : luecke[0]!)
    : null;
  if (fehlende) {
    host.appendChild(div("om-bilanz-warnung",
      `Unvollständige Datenbasis: ${fehlende} ${luecke!.length > 1 ? "melden" : "meldet"} in diesem Zeitraum keine Käufe an GA4. `
      + "Diese Umsätze fehlen in allen Zahlen unten, bei den kostenlosen Kanälen genauso wie bei den bezahlten. "
      + "Das Kauf-Tracking wurde am 04.08.2026 nachgerüstet — der Zeitraum reicht noch dahinter zurück."));
  }
  const max = Math.max(...gruppen.map((g) => Math.max(g.umsatz, g.kosten)), 1);
  const reihe = div("om-bilanz-cards");

  for (const g of gruppen) {
    const karte = div("om-bilanz-card");
    karte.style.setProperty("--bilanz-farbe", FARBE[g.gruppe]);

    const titel = g.gruppe === "kostenlos" ? "Kostenlose Kanäle" : "Bezahlte Kanäle";
    const unter = g.gruppe === "kostenlos"
      ? "Organische Suche, KI-Assistenten, Direkt, E-Mail, Empfehlungen"
      : "Google Ads, Meta Ads";
    karte.appendChild(div("om-bilanz-card__title", titel));
    karte.appendChild(div("om-bilanz-card__sub", unter));

    const balken = (bezeichnung: string, wert: number, art: string): HTMLElement => {
      const zeile = div("om-bilanz-bar");
      zeile.appendChild(div("om-bilanz-bar__label", bezeichnung));
      const spur = div("om-bilanz-bar__track");
      const fuell = div(`om-bilanz-bar__fill om-bilanz-bar__fill--${art}`);
      fuell.style.width = `${Math.max(1, (Math.abs(wert) / max) * 100)}%`;
      spur.appendChild(fuell);
      zeile.appendChild(spur);
      zeile.appendChild(div("om-bilanz-bar__val", eur(wert)));
      return zeile;
    };
    karte.appendChild(balken("Umsatz", g.umsatz, "umsatz"));
    karte.appendChild(balken("Kosten", g.kosten, "kosten"));

    const erg = div("om-bilanz-card__result");
    erg.classList.add(g.ergebnis >= 0 ? "is-plus" : "is-minus");
    erg.textContent = `${g.ergebnis >= 0 ? "+" : ""}${eur(g.ergebnis)}`;
    karte.appendChild(erg);
    karte.appendChild(div("om-bilanz-card__meta",
      `${zahl(g.kaeufe)} Käufe · ${zahl(g.sessions)} Sitzungen${g.roas !== null ? ` · ROAS ${g.roas.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×` : ""} — nach GA4${fehlende ? `, ohne ${fehlende}` : " (letzter Klick)"}`));

    // Bei den bezahlten Kanälen die Pixel-Sicht der Plattformen dagegenstellen:
    // GA4 kann Meta-Käufe wegen der Checkout-Domain praktisch nicht sehen.
    if (g.gruppe === "bezahlt" && ps) {
      const alt2 = div("om-bilanz-card__alt");
      const wert = div("om-bilanz-card__alt-val");
      wert.classList.add(ps.ergebnis >= 0 ? "is-plus" : "is-minus");
      wert.textContent = `${ps.ergebnis >= 0 ? "+" : ""}${eur(ps.ergebnis)}`;
      alt2.appendChild(wert);
      alt2.appendChild(div("om-bilanz-card__alt-meta",
        `${zahl(ps.kaeufe)} Käufe · ${eur(ps.wert)} Conversion-Wert${ps.roas !== null ? ` · ROAS ${ps.roas.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×` : ""} — laut Google Ads & Meta selbst, alle Portale`));
      karte.appendChild(alt2);
    }
    reihe.appendChild(karte);
  }
  host.appendChild(reihe);

  // Einordnungssatz — beantwortet die Ausgangsfrage direkt.
  const frei = gruppen.find((g) => g.gruppe === "kostenlos");
  const paid = gruppen.find((g) => g.gruppe === "bezahlt");
  if (frei && paid) {
    const grund = fehlende
      ? ` Der Abstand kommt überwiegend daher, dass GA4 die Verkäufe von ${fehlende} nicht erfasst — dort laufen die Meta-Kampagnen.`
      : " Beide Messungen zählen unterschiedlich: GA4 nach letztem Klick, die Plattformen per Pixel mit Sicht- und 7-Tage-Fenster.";
    const spanne = ps
      ? `Die bezahlten Kanäle stehen bei ${eur(paid.ergebnis)} nach GA4 und bei ${eur(ps.ergebnis)} nach der Plattform-Messung.${grund} `
      : "";
    const nachsatz = fehlende ? " — allerdings ohne die Verkäufe der oben genannten Portale, also eher zu niedrig" : "";
    const satz = `${spanne}Die kostenlosen Kanäle bringen ${eur(frei.umsatz)} bei ${zahl(frei.kaeufe)} Käufen ohne Mediakosten${nachsatz}.`;
    host.appendChild(div("om-bilanz-fazit", satz));
  }
}

function renderTabelle(host: HTMLElement, kanaele: Kanal[]): void {
  host.textContent = "";
  const tab = document.createElement("table");
  tab.className = "om-table om-bilanz-table";
  const kopf = tab.insertRow();
  for (const [text, cls] of [["Kanal", ""], ["Art", ""], ["Sitzungen", "num"], ["Käufe", "num"],
    ["Umsatz", "num"], ["Kosten", "num"], ["Ergebnis", "num"]] as Array<[string, string]>) {
    const th = document.createElement("th");
    th.textContent = text;
    if (cls) th.className = cls;
    kopf.appendChild(th);
  }
  for (const k of kanaele) {
    const tr = tab.insertRow();
    tr.className = `is-${k.gruppe}`;
    const zellen: Array<[string, string]> = [
      [label(k.kanal), ""],
      [k.gruppe === "bezahlt" ? "bezahlt" : "kostenlos", "om-bilanz-art"],
      [zahl(k.sessions), "num"],
      [zahl(k.kaeufe), "num"],
      [eur(k.umsatz), "num"],
      [k.kosten ? eur(k.kosten) : "—", "num"],
      [k.kosten ? eur(k.umsatz - k.kosten) : "—", "num"],
    ];
    zellen.forEach(([text, cls], i) => {
      const td = tr.insertCell();
      td.textContent = text;
      if (cls) td.className = cls;
      if (i === 6 && k.kosten) td.classList.add(k.umsatz - k.kosten >= 0 ? "is-plus" : "is-minus");
    });
  }
  host.appendChild(tab);
}

// Gegenüberstellung je Werbeplattform: was GA4 zuordnet vs. was die Plattform
// selbst meldet. Die Lücke ist der eigentliche Erkenntnisgewinn.
function renderPlattform(host: HTMLElement, liste: Plattform[], summe: PlattformSumme): void {
  host.textContent = "";
  host.appendChild(div("om-bilanz-subhead", "Bezahlte Kanäle: GA4 gegen die Plattform-Messung"));
  const tab = document.createElement("table");
  tab.className = "om-table om-bilanz-table";
  const kopf = tab.insertRow();
  for (const t of ["Plattform", "Kosten", "GA4: Käufe", "GA4: Umsatz", "GA4: Ergebnis",
    "Plattform: Käufe", "Plattform: Wert", "Plattform: Ergebnis", "ROAS"]) {
    const th = document.createElement("th");
    th.textContent = t;
    kopf.appendChild(th);
  }
  const zeile = (name: string, kosten: number, gk: number, gu: number, pk: number, pw: number, pe: number, roas: number | null, fett: boolean): void => {
    const tr = tab.insertRow();
    if (fett) tr.className = "is-summe";
    const ga4Erg = gu - kosten;
    const werte: Array<[string, "plus" | "minus" | ""]> = [
      [name, ""], [eur(kosten), ""], [zahl(gk), ""], [eur(gu), ""],
      [eur(ga4Erg), ga4Erg >= 0 ? "plus" : "minus"],
      [zahl(pk), ""], [eur(pw), ""], [eur(pe), pe >= 0 ? "plus" : "minus"],
      [roas !== null ? roas.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "×" : "—", ""],
    ];
    for (const [text, art] of werte) {
      const td = tr.insertCell();
      td.textContent = text;
      if (art) td.classList.add(`is-${art}`);
    }
  };
  for (const p of liste) zeile(p.name, p.kosten, p.ga4Kaeufe, p.ga4Umsatz, p.kaeufe, p.wert, p.ergebnis, p.roas, false);
  const gaSumme = liste.reduce((a, p) => a + p.ga4Umsatz, 0);
  const gaKaeufe = liste.reduce((a, p) => a + p.ga4Kaeufe, 0);
  zeile("Summe", summe.kosten, gaKaeufe, gaSumme, summe.kaeufe, summe.wert, summe.ergebnis, summe.roas, true);
  host.appendChild(tab);
}

export function initBilanzTab(m: HTMLElement, deps: MarketingDeps): void {
  const vergleichHost = m.querySelector("[data-bilanz-vergleich]") as HTMLElement | null;
  const tabelleHost = m.querySelector("[data-bilanz-tabelle]") as HTMLElement | null;
  const plattformHost = m.querySelector("[data-bilanz-plattform]") as HTMLElement | null;
  const infoHost = m.querySelector("[data-bilanz-info]") as HTMLElement | null;
  const msg = m.querySelector('[data-msg="bilanz"]') as HTMLElement | null;
  const wahl = m.querySelector("[data-bilanz-tage]") as HTMLSelectElement | null;
  const eigen = m.querySelector("[data-bilanz-custom]") as HTMLElement | null;
  const vonFeld = m.querySelector("[data-bilanz-von]") as HTMLInputElement | null;
  const bisFeld = m.querySelector("[data-bilanz-bis]") as HTMLInputElement | null;
  if (!vergleichHost || !tabelleHost) return;

  const setMsg = (t: string): void => { if (msg) msg.textContent = t; };

  // Aktuell gewählter Zeitraum als Query-String — dieselbe Form für Abruf und Start.
  const zeitraum = (): string => {
    if (wahl?.value === "custom" && vonFeld?.value && bisFeld?.value) {
      return `von=${encodeURIComponent(vonFeld.value)}&bis=${encodeURIComponent(bisFeld.value)}`;
    }
    return `tage=${encodeURIComponent(wahl?.value ?? "90")}`;
  };

  const leeren = (): void => {
    tabelleHost.textContent = "";
    if (plattformHost) plattformHost.textContent = "";
    if (infoHost) infoHost.textContent = "";
  };

  let pollTimer: number | undefined;

  // Für nicht vorberechnete Zeiträume den Motor anstoßen und auf ihn warten.
  const berechne = async (): Promise<void> => {
    setMsg("Berechne …");
    const r = await deps.api<{ stamp?: string }>(`/api/marketing/kanal-bilanz/run?${zeitraum()}`, "POST");
    if (r.status === 409) { setMsg("Eine Berechnung läuft bereits — einen Moment."); return; }
    if (r.status !== 200) { setMsg(`Fehler (${r.status})`); return; }
    const warte = async (): Promise<void> => {
      const jr = await deps.api<{ jobs: Array<{ status: string; log: string | null }> }>("/api/marketing/jobs?type=kanal_bilanz");
      const laeuft = jr.data?.jobs.some((j) => j.status === "running");
      if (!laeuft) { setMsg(""); await load(); return; }
      if (m.isConnected) pollTimer = window.setTimeout(() => { void warte(); }, 4000);
    };
    pollTimer = window.setTimeout(() => { void warte(); }, 4000);
  };

  const load = async (): Promise<void> => {
    const r = await deps.api<BilanzResponse>(`/api/marketing/kanal-bilanz?${zeitraum()}`);
    const d = r.data;
    if (r.status === 400) {
      vergleichHost.innerHTML = '<p class="dim">Bitte ein gültiges Datumspaar wählen (höchstens 400 Tage Abstand).</p>';
      leeren();
      return;
    }
    if (!d || d.empty) {
      vergleichHost.innerHTML = '<p class="dim">Noch keine Auswertung vorhanden. „Bilanz aktualisieren" zieht GA4, Google Ads und Meta.</p>';
      leeren();
      return;
    }
    if (d.fehlt) {
      vergleichHost.innerHTML = '<p class="dim">Für diesen Zeitraum liegt noch keine Auswertung vor — sie wird jetzt berechnet. Das dauert etwa eine Minute.</p>';
      leeren();
      void berechne();
      return;
    }
    renderVergleich(vergleichHost, d.gruppen, d.plattformSumme, d.ohneKaufTracking);
    if (plattformHost && d.plattform && d.plattformSumme) renderPlattform(plattformHost, d.plattform, d.plattformSumme);
    renderTabelle(tabelleHost, d.kanaele);
    if (infoHost) {
      infoHost.textContent = "";
      infoHost.appendChild(div("om-bilanz-zeitraum",
        `Zeitraum ${d.von} bis ${d.bis} (${d.tage} Tage) · Stand ${deps.formatDate(d.report_at)}`));
      const ul = document.createElement("ul");
      ul.className = "om-bilanz-hinweise";
      for (const h of d.hinweise ?? []) {
        const li = document.createElement("li");
        li.textContent = h;
        ul.appendChild(li);
      }
      infoHost.appendChild(ul);
    }
  };

  wahl?.addEventListener("change", () => {
    const istEigen = wahl.value === "custom";
    if (eigen) eigen.hidden = !istEigen;
    // Beim Umschalten auf „eigener Zeitraum" erst laden, wenn beide Daten stehen.
    if (istEigen) {
      if (vonFeld && !vonFeld.value && bisFeld && !bisFeld.value) {
        const bis = new Date(); bis.setDate(bis.getDate() - 1);
        const von = new Date(); von.setDate(von.getDate() - 30);
        vonFeld.value = von.toISOString().slice(0, 10);
        bisFeld.value = bis.toISOString().slice(0, 10);
      }
      return;
    }
    void load();
  });
  m.querySelector('[data-action="bilanz-custom"]')?.addEventListener("click", () => { void load(); });

  // „Bilanz aktualisieren" rechnet den aktuell gewählten Zeitraum neu.
  m.querySelector('[data-action="run-bilanz"]')?.addEventListener("click", () => { void berechne(); });
  m.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-backdrop")) window.clearTimeout(pollTimer);
  });

  void load();
}
